import { QuestionMarkCircleIcon } from "@heroicons/react/outline";

import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import { Toggle } from "@/components/Toggle";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BasicTooltip } from "@/components/ui/Tooltip";

interface MockToolResponsesToggleProps {
  enabled: boolean;
  scenario?: string | null;
  stopTools?: string | null;
  onToggle: (enabled: boolean) => void;
  onScenarioChange: (scenario: string) => void;
  onStopToolsChange: (tools: string) => void;
}

const MockToolResponsesToggle = ({
  enabled,
  scenario,
  stopTools,
  onToggle,
  onScenarioChange,
  onStopToolsChange,
}: MockToolResponsesToggleProps) => {
  const isReadonly = useIsReadonly();

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-700">Mock tool responses</span>
          <BasicTooltip content="Answer every tool call with an AI-generated mock instead of running tool code. Add an optional scenario to steer the mocks (e.g. make a tool fail).">
            <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400" />
          </BasicTooltip>
        </div>
        <Toggle enabled={enabled} setEnabled={onToggle} disabled={isReadonly} />
      </div>
      {enabled && (
        <>
          <Textarea
            value={scenario ?? ""}
            onChange={e => onScenarioChange(e.target.value)}
            placeholder="Scenario (optional), e.g. every tool fails with a timeout…"
            disabled={isReadonly}
            className="min-h-[72px] resize-none text-sm"
          />
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-600">
              Stop when the agent calls (optional)
            </span>
            <Input
              value={stopTools ?? ""}
              onChange={e => onStopToolsChange(e.target.value)}
              placeholder="Comma-separated tool names, e.g. submit, finish"
              disabled={isReadonly}
              className="text-sm"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default MockToolResponsesToggle;
